import { c as _c } from "react/compiler-runtime";
import type { MutableRefObject } from "react";
import CircleLoader, { CircleLoaderSize } from "@/components/distribution-plan-tool/common/CircleLoader";
import WaveDropsEmptyPlaceholder from "@/components/waves/drops/WaveDropsEmptyPlaceholder";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import type { ActiveDropState } from "@/types/dropInteractionTypes";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import type { useVirtualizedWaveDrops } from "@/hooks/useVirtualizedWaveDrops";
import { WaveDropsMessageListSection } from "./WaveDropsMessageListSection";
import { WaveDropsTypingIndicator } from "./WaveDropsTypingIndicator";
type WaveMessagesResult = ReturnType<typeof useVirtualizedWaveDrops>["waveMessages"];
interface WaveDropsContentProps {
  readonly waveMessages: WaveMessagesResult;
  readonly dropId: string | null;
  readonly scrollContainerRef: MutableRefObject<HTMLDivElement | null>;
  readonly bottomAnchorRef: MutableRefObject<HTMLDivElement | null>;
  readonly onTopIntersection: () => void;
  readonly onReply: ({
    drop,
    partId
  }: {
    drop: ApiDrop;
    partId: number;
  }) => void;
  readonly onQuote: ({
    drop,
    partId
  }: {
    drop: ApiDrop;
    partId: number;
  }) => void;
  readonly queueSerialTarget: (serialNo: number) => void;
  readonly activeDrop: ActiveDropState | null;
  readonly serialTarget: number | null;
  readonly targetDropRef: MutableRefObject<HTMLDivElement | null>;
  readonly onQuoteClick: (drop: ApiDrop) => void;
  readonly isAtBottom: boolean;
  readonly scrollToBottom: () => void;
  readonly typingMessage: string | null;
  readonly onDropContentClick?: (drop: ExtendedDrop) => void;
  readonly pendingCount: number;
  readonly onRevealPending: () => void;
}
export const WaveDropsContent: React.FC<WaveDropsContentProps> = t0 => {
  const $ = _c(26);
  const {
    waveMessages,
    dropId,
    scrollContainerRef,
    bottomAnchorRef,
    onTopIntersection,
    onReply,
    onQuote,
    queueSerialTarget,
    activeDrop,
    serialTarget,
    targetDropRef,
    onQuoteClick,
    isAtBottom,
    scrollToBottom,
    typingMessage,
    onDropContentClick,
    pendingCount,
    onRevealPending
  } = t0;
  const dropsCount = waveMessages?.drops?.length ?? 0;
  const isInitialLoading = !!waveMessages?.isLoading && !waveMessages?.isLoadingNextPage && dropsCount === 0;
  const isHydrating = !waveMessages;
  if (isHydrating || isInitialLoading) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-10"><CircleLoader size={CircleLoaderSize.XXLARGE} /></div>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  if (dropsCount === 0) {
    let t1;
    if ($[1] !== dropId) {
      t1 = <WaveDropsEmptyPlaceholder dropId={dropId} />;
      $[1] = dropId;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    return t1;
  }
  let t1;
  if ($[3] !== activeDrop || $[4] !== bottomAnchorRef || $[5] !== dropId || $[6] !== isAtBottom || $[7] !== onDropContentClick || $[8] !== onQuote || $[9] !== onQuoteClick || $[10] !== onReply || $[11] !== onRevealPending || $[12] !== onTopIntersection || $[13] !== pendingCount || $[14] !== queueSerialTarget || $[15] !== scrollContainerRef || $[16] !== scrollToBottom || $[17] !== serialTarget || $[18] !== targetDropRef || $[19] !== waveMessages) {
    t1 = <WaveDropsMessageListSection waveMessages={waveMessages} dropId={dropId} scrollContainerRef={scrollContainerRef} bottomAnchorRef={bottomAnchorRef} onTopIntersection={onTopIntersection} onReply={onReply} onQuote={onQuote} queueSerialTarget={queueSerialTarget} activeDrop={activeDrop} serialTarget={serialTarget} targetDropRef={targetDropRef} onQuoteClick={onQuoteClick} isAtBottom={isAtBottom} scrollToBottom={scrollToBottom} onDropContentClick={onDropContentClick} pendingCount={pendingCount} onRevealPending={onRevealPending} />;
    $[3] = activeDrop;
    $[4] = bottomAnchorRef;
    $[5] = dropId;
    $[6] = isAtBottom;
    $[7] = onDropContentClick;
    $[8] = onQuote;
    $[9] = onQuoteClick;
    $[10] = onReply;
    $[11] = onRevealPending;
    $[12] = onTopIntersection;
    $[13] = pendingCount;
    $[14] = queueSerialTarget;
    $[15] = scrollContainerRef;
    $[16] = scrollToBottom;
    $[17] = serialTarget;
    $[18] = targetDropRef;
    $[19] = waveMessages;
    $[20] = t1;
  } else {
    t1 = $[20];
  }
  let t2;
  if ($[21] !== typingMessage) {
    t2 = <WaveDropsTypingIndicator typingMessage={typingMessage} />;
    $[21] = typingMessage;
    $[22] = t2;
  } else {
    t2 = $[22];
  }
  let t3;
  if ($[23] !== t1 || $[24] !== t2) {
    t3 = <>{t1}{t2}</>;
    $[23] = t1;
    $[24] = t2;
    $[25] = t3;
  } else {
    t3 = $[25];
  }
  return t3;
};